/**
 * Contract Review Modal Component
 *
 * Modal for reviewing signed contracts before company counter-signature.
 * Shows read-only contract display with void and sign actions.
 *
 * Used by: RecruitmentDrawer when "Review Contract" button is clicked
 *
 * Workflow:
 * 1. LO signs contract → Status becomes "recruit_signed"
 * 2. Admin clicks "Review Contract" → Opens this modal
 * 3. Admin can:
 *    a) Void contract (if issues found) → Creates new version
 *    b) Sign contract (if approved) → Company counter-signs
 */

import { useState, useRef } from 'react';
import { LwModal } from '@/components/lw';
import { FileX, FileSignature, Loader2, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import type { ContractInstanceData, RecruitmentCandidate } from '@/types/recruitment';
import { ReadOnlyContractDisplay } from './ReadOnlyContractDisplay';
import { VoidContractDialog } from './VoidContractDialog';
import { CompanySignatureModal } from './CompanySignatureModal';
import { canVoidContract } from '@/services/contractVersionService';
import { createContractInstance } from '@/services/recruitmentService';
import { useAuth } from '@/contexts/useAuth';
import { useRecruitmentTemplate } from '@/hooks/useRecruitment';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ContractReviewModal', 'recruitment');

interface ContractReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: RecruitmentCandidate;
  contractInstance: ContractInstanceData | null | undefined;
  loading?: boolean;
  onRefresh: () => void;
}

export const ContractReviewModal: React.FC<ContractReviewModalProps> = ({
  open,
  onOpenChange,
  candidate,
  contractInstance,
  loading = false,
  onRefresh
}) => {
  const { user } = useAuth();
  const { template } = useRecruitmentTemplate();
  const contentRef = useRef<HTMLDivElement>(null);

  // Extract user role from roles array (admin or recruiter)
  const userRole = user?.roles?.includes('admin') ? 'admin'
    : user?.roles?.includes('recruiter') ? 'recruiter'
    : '';

  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Print/Export contract as PDF
  const handlePrint = () => {
    const printContent = contentRef.current;
    if (!printContent) {
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return;
    }

    const contractTitle = `Employment Contract - ${candidate.firstName} ${candidate.lastName}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${contractTitle}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; line-height: 1.6; }
            .contract-section { margin-bottom: 24px; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; }
            .contract-section h3 { font-size: 14px; font-weight: 600; margin-bottom: 12px; color: #374151; }
            .signature-block { margin-top: 24px; padding: 16px; background: #f9fafb; border-radius: 8px; }
            .signature-image { max-height: 60px; margin: 8px 0; }
            table { width: 100%; border-collapse: collapse; }
            td, th { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: left; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1 style="font-size: 24px; margin-bottom: 8px;">${contractTitle}</h1>
          <p style="color: #6b7280; margin-bottom: 24px;">Printed on ${new Date().toLocaleDateString()}</p>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Check if contract can be voided (only when contract exists)
  const voidValidation = contractInstance ? canVoidContract(contractInstance, userRole) : { allowed: false, reason: 'No contract loaded' };
  const canVoid = voidValidation.allowed;

  // Check if company can sign (LO must sign first, company hasn't signed yet, not voided)
  const canSign = contractInstance
    ? contractInstance.hasLoSignature &&
      !contractInstance.hasRecruiterSignature &&
      contractInstance.status !== 'voided'
    : false;

  // Check if we're in a loading state or missing data
  const isLoading = loading || !contractInstance;

  const handleVoidSuccess = () => {
    // Close this modal and refresh parent
    onOpenChange(false);
    onRefresh();
  };

  const handleSignSuccess = () => {
    // Close this modal and refresh parent
    onOpenChange(false);
    onRefresh();
  };

  // Handle regenerating a voided contract
  const handleRegenerate = async () => {
    if (!candidate?.id || !user?.id || !contractInstance) {
      toast.error('Unable to regenerate contract', {
        description: 'Missing required data'
      });
      return;
    }

    try {
      setIsRegenerating(true);

      const templateVersion = template?.version ?? 1;

      // Create new contract instance from the voided one
      await createContractInstance(
        {
          organization_id: user.organization_id!,
          recruit_id: candidate.id,
          template_id: template?.id,
          template_version: templateVersion,
          sections: contractInstance.sections || template?.sections || [],
          variable_data: contractInstance.variableData || {},
          checkbox_states: {}, // Reset checkboxes for new contract
          version_number: (contractInstance.versionNumber || 1) + 1,
          parent_instance_id: contractInstance.id,
          is_active: true,
          status: 'draft'
        },
        user.id
      );

      log.info('Contract regenerated successfully', {
        candidateId: candidate.id,
        previousVersion: contractInstance.versionNumber,
        newVersion: (contractInstance.versionNumber || 1) + 1
      });

      toast.success('Contract regenerated successfully', {
        description: 'A new draft contract has been created'
      });

      // Close modal and refresh parent
      onOpenChange(false);
      onRefresh();
    } catch (error) {
      log.error('Failed to regenerate contract', {
        error,
        candidateId: candidate.id
      });
      toast.error('Failed to regenerate contract', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <>
      {open && (
        <LwModal
          size="tall"
          eyebrow="Contract"
          title={isLoading ? 'Review Contract' : `Review Contract - ${candidate.firstName} ${candidate.lastName}`}
          sub={isLoading ? undefined : 'Review the signed contract before counter-signing or voiding'}
          onClose={() => onOpenChange(false)}
          footer={
            isLoading ? undefined : (
              <>
                <button className="btn-sm" style={{ marginRight: 'auto' }} onClick={handlePrint} type="button">
                  <Download />
                  Export PDF
                </button>
                {canVoid && (
                  <button className="btn-sm danger" onClick={() => setVoidDialogOpen(true)} type="button">
                    <FileX />
                    Void Contract
                  </button>
                )}
                <button className="btn-sm" onClick={() => onOpenChange(false)} type="button">
                  Close
                </button>
                {canSign && (
                  <button className="btn-sm solid" onClick={() => setSignatureModalOpen(true)} type="button">
                    <FileSignature />
                    Sign Contract
                  </button>
                )}
                {contractInstance?.status === 'voided' && (
                  <button className="btn-sm solid" onClick={handleRegenerate} disabled={isRegenerating} type="button">
                    {isRegenerating ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                    {isRegenerating ? 'Regenerating…' : 'Regenerate Contract'}
                  </button>
                )}
              </>
            )
          }
        >
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 256, gap: 16 }}>
              <Loader2 className="animate-spin" style={{ width: 32, height: 32, color: 'var(--lw-muted)' }} />
              <span style={{ fontSize: 13, color: 'var(--lw-muted)' }}>Loading contract...</span>
            </div>
          ) : (
            <>
              <div ref={contentRef}>
                <ReadOnlyContractDisplay contractInstance={contractInstance} showSignatures={true} />
              </div>

              {/* Helper Text */}
              {!canVoid && !canSign && (
                <div style={{ fontSize: 13, color: 'var(--lw-muted)', textAlign: 'center' }}>
                  {contractInstance.hasRecruiterSignature
                    ? '✓ Contract fully executed - both parties have signed'
                    : contractInstance.status === 'voided'
                      ? 'This contract has been voided. Click "Regenerate Contract" to create a new draft.'
                      : voidValidation.reason}
                </div>
              )}
            </>
          )}
        </LwModal>
      )}

      {/* Void Contract Dialog - only render when contract exists */}
      {contractInstance && (
        <VoidContractDialog
          open={voidDialogOpen}
          onOpenChange={setVoidDialogOpen}
          contractInstance={contractInstance}
          onVoidSuccess={handleVoidSuccess}
        />
      )}

      {/* Company Signature Modal - only render when contract exists */}
      {contractInstance && (
        <CompanySignatureModal
          open={signatureModalOpen}
          onOpenChange={setSignatureModalOpen}
          candidate={candidate}
          contractInstance={contractInstance}
          onSignSuccess={handleSignSuccess}
        />
      )}
    </>
  );
};
